import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { domainService, DomainAvailability } from '../../services/domainService';
import { useAuth } from '../../contexts/AuthContext';
import CheckoutSteps from '../../components/checkout/CheckoutSteps';
import ContactStep from '../../components/checkout/ContactStep';
import DomainPlanStep from '../../components/checkout/DomainPlanStep';
import DomainPaymentStep from '../../components/checkout/DomainPaymentStep';
import PasswordSetupStep from '../../components/checkout/PasswordSetupStep';
import DomainConfirmationStep from '../../components/checkout/DomainConfirmationStep';

export interface DomainCheckoutData {
  domainName: string;
  billingCycle: 'yearly';
  contactInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    company?: string;
    address: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
    };
  };
  pricing: {
    domainPrice: number;
    discount: number;
    total: number;
  };
  customerType?: 'new' | 'existing' | 'unknown';
  isExistingCustomer?: boolean;
  isNewCustomerAccountCreated?: boolean;
  orderId?: string;
  orderNumber?: string;
  paymentIntentId?: string;
}

const DomainCheckout: React.FC = () => {
  const { domainName } = useParams<{ domainName: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [domain, setDomain] = useState<DomainAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const [checkoutData, setCheckoutData] = useState<DomainCheckoutData>({
    domainName: domainName || '',
    billingCycle: 'yearly',
    contactInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
      },
    },
    pricing: {
      domainPrice: 0,
      discount: 0,
      total: 0,
    },
  });

  useEffect(() => {
    const initializeCheckout = async () => {
      if (!domainName) {
        navigate('/domains');
        return;
      }

      // Check if user is signed in - if not, show login prompt
      if (!user && !localStorage.getItem('token')) {
        setShowLoginPrompt(true);
        setLoading(false);
        // Still create a basic domain object for the UI
        setDomain({
          domainName: domainName,
          available: true,
          price: 15.00 // Default price
        });
        return;
      }

      setLoading(true);
      try {
        // Check if domain data was passed in navigation state
        if (location.state?.domain) {
          const domainData = location.state.domain as DomainAvailability;
          setDomain(domainData);
          updatePricing(domainData);
        } else {
          // If no state, fetch domain availability
          const response = await domainService.checkDomainAvailability(domainName);
          if (response.success && response.data) {
            if (!response.data.available) {
              setError('Domain is not available for registration');
              return;
            }
            setDomain(response.data);
            updatePricing(response.data);
          } else {
            setError(response.error || 'Failed to check domain availability');
            return;
          }
        }
      } catch (error) {
        console.error('Error initializing domain checkout:', error);
        setError('Failed to initialize checkout');
      } finally {
        setLoading(false);
      }
    };

    initializeCheckout();
  }, [domainName, location.state, navigate, user]);

  // Populate user data if signed in. AWS RegisterDomain requires a full
  // postal address + phone, so copy those from the user's profile too.
  // Without this, the order's contact_info.address ends up empty strings
  // and the provisioning service can't submit the registration to AWS.
  useEffect(() => {
    if (user && !showLoginPrompt) {
      const ua = (user as any).address || {};
      setCheckoutData(prev => ({
        ...prev,
        contactInfo: {
          ...prev.contactInfo,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          phone: user.phone || '',
          company: (user as any).companyName || prev.contactInfo.company || '',
          address: {
            street: ua.street || ua.addressLine1 || prev.contactInfo.address.street || '',
            city: ua.city || prev.contactInfo.address.city || '',
            state: ua.state || prev.contactInfo.address.state || '',
            zipCode: ua.zipCode || ua.zip || prev.contactInfo.address.zipCode || '',
            country: ua.country || ua.countryCode || prev.contactInfo.address.country || '',
          },
        },
        customerType: 'existing',
        isExistingCustomer: true
      }));
    }
  }, [user, showLoginPrompt]);

  const updatePricing = (domainData: DomainAvailability) => {
    const domainPrice = domainData.price || 12.99;
    const discount = 0; // No discount for domain-only purchases
    const total = domainPrice;

    setCheckoutData(prev => ({
      ...prev,
      // Prefer the value the backend echoes back; fall back to the URL param
      // so we never lose the domain name when populating checkout state.
      domainName: domainData.domainName || domainName || prev.domainName,
      pricing: {
        domainPrice,
        discount,
        total,
      },
    }));
  };

  const getSteps = () => {
    const isSignedIn = !!user && !!localStorage.getItem('token');
    
    if (isSignedIn) {
      // For signed-in users: Skip contact step
      return [
        { number: 1, title: 'Review Domain', description: 'Confirm registration' },
        { number: 2, title: 'Payment', description: 'Secure payment' },
        { number: 3, title: 'Confirmation', description: 'Domain registered' }
      ];
    } else {
      // For non-signed-in users: Show login prompt first
      return [
        { number: 1, title: 'Sign In', description: 'Account required' },
        { number: 2, title: 'Review Domain', description: 'Confirm registration' },
        { number: 3, title: 'Payment', description: 'Secure payment' },
        { number: 4, title: 'Confirmation', description: 'Domain registered' }
      ];
    }
  };

  const steps = getSteps();

  const handleStepComplete = (stepData: any) => {
    setCheckoutData(prev => ({ ...prev, ...stepData }));
    setCurrentStep(prev => prev + 1);
    window.scrollTo(0, 0);
  };

  const handleStepBack = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
    window.scrollTo(0, 0);
  };

  const updateCheckoutData = (data: Partial<DomainCheckoutData>) => {
    setCheckoutData(prev => ({ ...prev, ...data }));
  };

  const handleLoginRedirect = () => {
    const currentUrl = window.location.pathname;
    const returnUrl = encodeURIComponent(currentUrl);
    navigate(`/login?return=${returnUrl}&message=Please sign in to purchase a domain`);
  };

  const LoginPrompt = () => (
    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
      {/* Glossy overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
      <div className="relative z-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-2xl mb-6 shadow-lg relative overflow-hidden mx-auto">
          <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
          <svg className="w-8 h-8 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Required</h2>
        <p className="text-gray-600 mb-8 max-w-md mx-auto">
          To purchase a domain, you'll need a 12and22 account. This helps us manage your domain registration and provide ongoing support.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleLoginRedirect}
            className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
            <span className="relative z-10">Sign In to Continue</span>
          </button>
          
          <button
            onClick={() => navigate('/register', { 
              state: { returnUrl: window.location.pathname }
            })}
            className="border-2 border-orange-400 text-orange-600 px-8 py-3 rounded-lg font-semibold text-lg hover:bg-gradient-to-br hover:from-orange-400 hover:via-yellow-500 hover:to-orange-600 hover:text-white hover:border-transparent transition-all duration-200 shadow-lg relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-transparent group-hover:from-white/20 transition-all duration-200 rounded-lg"></div>
            <span className="relative z-10">Create Account</span>
          </button>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Why do I need an account?</h4>
          <ul className="text-sm text-blue-800 space-y-1 text-left">
            <li>• Manage your domain settings and renewals</li>
            <li>• Access DNS management and domain transfer tools</li>
            <li>• Receive important notifications about your domain</li>
            <li>• Get priority customer support</li>
          </ul>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-2xl mb-4 shadow-lg relative overflow-hidden mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white relative z-10"></div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Loading Domain Checkout</h3>
              <p className="text-gray-600">Preparing your domain registration...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
            <div className="relative z-10 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-500 via-red-600 to-red-700 rounded-2xl mb-4 shadow-lg relative overflow-hidden mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                <svg className="h-8 w-8 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Checkout Error</h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={() => navigate('/domains')}
                className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-lg font-semibold hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
                <span className="relative z-10">Back to Domain Search</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!domain) {
    return null;
  }

  // Show login prompt if user is not authenticated
  if (showLoginPrompt) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
              <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
              <span className="text-sm font-medium text-gray-700">Domain Registration</span>
            </div>
            <h1 className="text-4xl font-black text-gray-900 mb-4">
              Register Your Domain
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">
                {domainName}
              </span>
            </h1>
            <p className="text-xl text-gray-600">
              Let's get you set up with your perfect domain name
            </p>
          </div>

          <CheckoutSteps steps={steps} currentStep={currentStep} />
          
          <div className="mt-8">
            <LoginPrompt />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
            <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
            <span className="text-sm font-medium text-gray-700">Domain Registration</span>
          </div>
          <h1 className="text-4xl font-black text-gray-900 mb-4">
            Complete Your Order
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">
              {domain.domainName}
            </span>
          </h1>
          <p className="text-xl text-gray-600">
            You're almost done! Let's finalize your domain registration
          </p>
        </div>

        <CheckoutSteps steps={steps} currentStep={currentStep} />

        <div className="mt-8">
          {(() => {
            const isSignedIn = !!user && !!localStorage.getItem('token');
            
            if (isSignedIn) {
              // Signed-in user flow: Review -> Payment -> Confirmation
              if (currentStep === 1) {
                return (
                  <DomainPlanStep
                    data={checkoutData}
                    domainName={domain.domainName}
                    domainPrice={domain.price || 12.99}
                    onNext={handleStepComplete}
                    onBack={() => navigate('/domains')}
                  />
                );
              } else if (currentStep === 2) {
                return (
                  <DomainPaymentStep
                    data={checkoutData}
                    onNext={handleStepComplete}
                    onBack={handleStepBack}
                  />
                );
              } else if (currentStep === 3) {
                return (
                  <DomainConfirmationStep
                    data={checkoutData}
                  />
                );
              }
            }
            
            return null;
          })()}
        </div>
      </div>
    </div>
  );
};

export default DomainCheckout;